import React, { useState, useRef, useEffect } from "react";
import {
  McInput,
  McSelect,
  McButton,
  McIcon,
  McOption,
} from "@maersk-global/mds-react-wrapper";
import "./CSS/FilterMenu.css";

function FilterMenu({
  columns,
  operators,
  data,
  onApply,
  onCancel,
  setIsMenuOpen,
  setActiveFilters,
  activeFilters = [],
}) {
  const [conditions, setConditions] = useState(() => {
    // Initialize with existing active filters if they exist, otherwise use empty condition
    if (activeFilters && activeFilters.length > 0) {
      return activeFilters.map(filter => ({
        field: filter.field || "",
        operator: filter.operator || "",
        value: filter.value || ""
      }));
    }
    return [{ field: "", operator: "", value: "" }];
  });
  // Create a ref for the scrollable container
  const conditionsContainerRef = useRef(null);

  const handleFieldChange = (index, value) => {
    const newConditions = [...conditions];
    newConditions[index].field = value;
    setConditions(newConditions);
  };

  const handleOperatorChange = (index, value) => {
    const newConditions = [...conditions];
    newConditions[index].operator = value;
    setConditions(newConditions);
  };

  const handleValueChange = (index, value) => {
    const newConditions = [...conditions];
    newConditions[index].value = value;
    setConditions(newConditions);
  };

  const addCondition = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setConditions([...conditions, { field: "", operator: "", value: "" }]);

    // Schedule scrolling to the bottom after state update
    setTimeout(() => {
      if (conditionsContainerRef.current) {
        conditionsContainerRef.current.scrollTop =
          conditionsContainerRef.current.scrollHeight;
      }
    }, 0);
  };

  const removeCondition = (index, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    const newConditions = [...conditions];
    newConditions.splice(index, 1);
    setConditions(newConditions);
    return false;
  };

  const handleApply = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const validConditions = conditions.filter(
      (condition) =>
        condition.field && condition.operator && condition.value.trim()
    );
    onApply && onApply(validConditions);
    setIsMenuOpen && setIsMenuOpen(false);
  };

  return (
    <div className="filter-menu" onClick={(e) => e.stopPropagation()}>
      <div className="filter-menu-subtitle">
        <p>Select and add conditions</p>
      </div>
      <div
        ref={conditionsContainerRef}
        style={{
          display: "flex",
          flexDirection: "column",
          maxHeight: "20vh",
          overflowY: "auto",
        }}
      >
        {conditions.map((condition, index) => (
          <div key={index} className="filter-condition">
            {index === 0 && (
              <div className="condition-label">
                <span>Where</span>
              </div>
            )}
            {index > 0 && (
              <div className="condition-label">
                <span>And</span>
              </div>
            )}

            <div className="condition-fields">
              <McSelect
                optionsheight="200px"
                hiddenlabel
                value={condition.field}
                optionselected={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleFieldChange(index, e.detail.value);
                }}
                placeholder="Select field"
              >
                {columns
                  .filter((col) => col.id && col.id.trim() !== "" && col.id !== "id")
                  .map((col) => (
                    <McOption key={col.id} value={col.id}>
                      {col.label || col.id}
                    </McOption>
                  ))}
              </McSelect>

              <McSelect
                optionsheight="200px"
                hiddenlabel
                value={condition.operator}
                optionselected={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleOperatorChange(index, e.detail.value);
                }}
                placeholder="Select operator"
              >
                {operators.map((op) => (
                  <McOption key={op.value} value={op.value}>
                    {op.label}
                  </McOption>
                ))}
              </McSelect>

              <McInput
                hiddenlabel
                value={condition.value}
                input={(e) => handleValueChange(index, e.target.value)}
                placeholder="Enter value"
                aria-label="Value"
              />

              <div
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (conditions.length > 1) {
                    removeCondition(index, e);
                  }
                }}
                style={{
                  cursor: conditions.length === 1 ? "not-allowed" : "pointer",
                  opacity: conditions.length === 1 ? 0.5 : 1,
                  padding: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <McIcon icon="times" />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="filter-actions">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <McButton
            label="Add Conditions"
            appearance="neutral"
            icon="plus"
            click={addCondition}
          />
          <McButton
            label="Clear Filters"
            appearance="error"
            variant="outlined"
            fit="medium"
            click={() => {
              setConditions([{ field: "", operator: "", value: "" }]);
              setActiveFilters([]);
              // Apply empty filters immediately to clear the table
              onApply && onApply([]);
            }}
          />
        </div>

        <McButton
          appearance="primary"
          label="Apply"
          fit="medium"
          click={handleApply}
        />
      </div>
    </div>
  );
}

export default FilterMenu;
